<template>
  <div>
    <wc-page-title title='Ready for Office' subtitle='(completed by crews)'></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12'>
          <wc-data-box mini title='Work Orders ready for Invoice Creation' showHelp :helpText='wosHelpText'>
            <template v-slot:prependToolbarButtons>
              <span class='caption mr-2'>{{ completed + ' processed. ' + (wos.length - completed) + ' remaining' }}</span>
              <v-progress-circular :value='processed' color='green' size='24' rotate='270'
                width='6'></v-progress-circular>
              <span class='caption ml-2'>{{ processed + '% Complete' }}</span>
              <v-btn icon x-small :disabled='page === 1' class='ml-6' @click='first'>
                <v-icon>mdi-chevron-double-left</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled='page === 1' class='mr-2' @click='prev'>
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class='caption' v-if='!search'>
                {{ pageStart }} - {{ pageEnd }} of {{ wos.length }}
              </span>
              <span class='caption' v-else>searching</span>
              <v-btn icon x-small :disabled='page === pageCount' class='ml-2' @click='next'>
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled='page === pageCount' class='mr-4' @click='last'>
                <v-icon>mdi-chevron-double-right</v-icon>
              </v-btn>
              <wc-toolbar-search v-model='search'></wc-toolbar-search>
            </template>
            <v-data-table dense :items='wos' :headers='headers' :loading='isLoading' :search='search'
              :items-per-page='itemsPerPage' :item-class='getClass' :page.sync='page'
              no-data-text='No completed Work Orders found' @click:row='open' hide-default-footer>
              <template v-slot:item.date_completed='{ item }'>
                {{ item.date_completed | shortDate }}
              </template>
              <template v-slot:item.wo_type='{ item }'>
                {{ getWOType(item).name }}
                <v-icon v-if='item.office_complete && item.invoiceID' color='grey lighten-1' small
                  right>mdi-open-in-new</v-icon>
              </template>
            </v-data-table>
          </wc-data-box>
        </v-col>

        <v-col cols='12'>
          <wo-processing-lines v-if='wo' :isLoading='isLoadingWO || isSavingLine' :wo='wo' :product='product'
            :labour='labour' :productLines='productLines' :labourLines='labourLines' @saveProductLine='saveProductLine'
            @saveLabourLine='saveLabourLine' @deleteProductLine='deleteProductLine'
            @deleteLabourLine='deleteLabourLine'></wo-processing-lines>
        </v-col>

        <v-col cols='12' md='4'>
          <crew-visits v-if='wo' :wo='wo'></crew-visits>
          <outstanding-service
            class='mt-6'
            v-if='wo && wo.project'
            :project='wo.project_obj'
            disableAdd
          ></outstanding-service>
        </v-col>

        <v-col cols='12' md='4'>
          <wc-data-box v-if='wo' mini title='Original Problem Description'>
            <v-card-text style='padding-bottom: 6px;' v-html='wo.problem_description'></v-card-text>
          </wc-data-box>

          <resolution class='mt-6' v-if='wo' :wo='wo' :labourLines='labourLines' @update='updateWO'></resolution>

          <stickies ref='stickies' class='mt-6' v-if='wo && (wo.project_obj || wo.estimate_obj)'
            :project='wo.project_obj' :estimate='wo.estimate_obj'></stickies>
        </v-col>

        <v-col cols='12' md='4'>
          <wc-data-box v-if='wo' mini title="Totals" showHelp :helpText='totalsHelpText'>
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td :class='productIcon.color + "--text overline"'>Product</td>
                  <td :class='productIcon.color + "--text text-right font-weight-bold"'>
                    {{ productTotal | decMoney }}
                  </td>
                </tr>
                <tr>
                  <td :class='labourIcon.color + "--text overline"'>Labour</td>
                  <td :class='labourIcon.color + "--text text-right font-weight-bold"'>
                    {{ labourTotal | decMoney }}
                  </td>
                </tr>
                <tr v-if="wo.timeline === 'BM8'">
                  <td class="error--text overline">BM8 Fee</td>
                  <td class="error--text text-right font-weight-bold">
                    {{ 20 | decMoney }}
                  </td>
                </tr>
                <tr v-if="wo.timeline === 'PRI'">
                  <td class="error--text overline">Priority Fee</td>
                  <td class="error--text text-right font-weight-bold">
                    {{ 40 | decMoney }}
                  </td>
                </tr>
                <tr>
                  <td class="grey--text overline">{{ 'Min Price (' + wo.wo_type_display + ')' }}</td>
                  <td class="grey--text text-right font-weight-bold">
                    {{ wo.minimum_price | decMoney }}
                  </td>
                </tr>
                <tr>
                  <td class="grey lighten-4 font-weight-bold overline">Invoice Total</td>
                  <td class="grey lighten-4 text-right font-weight-bold">
                    {{ invoiceTotal | decMoney }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-divider></v-divider>
            <v-card-text>
              <v-icon :color="wo.hold_invoice ? $store.state.icons.dataBox.invoiceHolds.color : 'grey'">
                {{ wo.hold_invoice ? $icons.hold.icon : 'mdi-thumb-up' }}
              </v-icon>
              {{ wo.hold_invoice ? "A hold will be applied to invoice" : "No holds" }}
            </v-card-text>
            <v-card-text>
              <span v-if='wo.po' class='success--text font-weight-bold'>{{ "PO: " + wo.po }}</span>
              <span v-else class='grey--text font-italic'>No PO set</span>
            </v-card-text>
            <v-card-text v-if='wo.hold_invoice_reason'>
              <strong>Invoice Hold Reason:</strong> {{ wo.hold_invoice_reason }}
            </v-card-text>
            <v-card-text v-if='suNotes.length'>
              <div v-for='note in suNotes' :key='note.id'>
                One Time SU Notes: {{ note.note_year }} - {{ getNoteTypeDisplay(note.note_type) }} - {{ note.note }}
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn block color='primary' elevation='0'
                :disabled='disableGenerate || isLoadingWO || allWOsProcessed || !!stickies.length || noBillingContact'
                @click='processWO'>{{ buttonText }}
              </v-btn>
            </v-card-actions>
          </wc-data-box>
        </v-col>

      </v-row>
      <work-order-tracker ref='actionTracker' v-if='wo !== null' :work_order='wo' />

    </v-container>
  </div>
</template>

<script>
import WorkOrderTracker from '@/components/trackers/WorkOrderTracker';
import _ from 'lodash';
import { mapState } from 'vuex';
import moment from 'moment';
import CrewVisits from '@/modules/wos/views/woDetail/CrewVisits';
import WOProcessingLines from '@/modules/wos/components/WOProcessingLines';
import WOResolution from '@/modules/wos/components/WOResolution';
import irrigationAPI from '@/modules/irrigation/api/irrigations';
import trackerAPI from '@/api/tracker';
import servicesAPI from '@/modules/assets/api/services';
import assetsAPI from '@/modules/assets/api/inventory';
import wosAPI from '@/modules/wos/api/index';
import miscServices from '@/services/misc';
import Stickies from '@/modules/projects/components/Stickies';
import OutstandingService from '@/modules/projects/components/OutstandingService';

export default {
  name: 'WOsReadyForOffice',
  data: () => ({
    isLoading: true,
    isLoadingWO: false,
    isSavingLine: false,
    disableGenerate: true,
    itemsPerPage: 4,
    search: '',
    invoice: null,
    notes: [],
    oneTimeNote: '',
    completed: 0,
    product: [],
    labour: [],
    productLines: [],
    labourLines: [],
    page: 1,
    totalsHelpText: "GST will be applied on the invoice. <br> These amounts can be edited once the invoice is created. <br><br> The minimum price for this work order type is defined in the settings module.",
    wosHelpText: "Click on a row to select a work order to process. (the first work order in the list is automatically selected and highlighted in blue when you enter this page) <br><br> Details for that work order will be shown in the lower section of the page. <br><br> Creating an invoice will mark that work order as completed (highlighted green and greyed out) and the next one will be automatically selected. <br><br> Clicking on a completed work order will open the newly generated invoice in a new window.<br><br>Please note that your progress will be lost if you close this page. (Each time this page is reloaded it will only include unprocessed work orders)",
    wos: [],
    wo: null,
    actions: [],
    headers: [
      { text: 'Number', value: 'number', width: '160' },
      { text: 'Project / Estimate', value: 'project_name' },
      { text: 'Address', value: 'project_site_address' },
      { text: 'City', value: 'project_site_city' },
      { text: 'Date Completed', value: 'date_completed' },
      { text: 'Type', value: 'wo_type' }
    ],
    trackerHeaders: [
      { text: 'Date', value: 'action_date', width: '130' },
      { text: 'Taken by', value: 'taken_by_obj.name_display', width: '170' },
      { text: 'Action', value: 'description' }
    ]
  }),
  mounted() {
    this.getData()
  },
  computed: {
    ...mapState({
      productIcon: state => state.icons.modules.inventory,
      labourIcon: state => state.icons.modules.labour
    }),
    divisions() {
      return this.$store.state.misc.divisions
    },
    woTypes() {
      return _.orderBy(this.$store.state.misc.woTypes, 'name')
    },
    pageCount() {
      return Math.floor(this.wos.length / this.itemsPerPage) + (this.wos.length % this.itemsPerPage ? 1 : 0)
    },
    pageStart() {
      if (this.wos.length) {
        return (this.itemsPerPage * this.page) - this.itemsPerPage + 1
      }
      return 0
    },
    pageEnd() {
      if (this.itemsPerPage * this.page > this.wos.length) {
        return (this.itemsPerPage * (this.page - 1)) + (this.wos.length % this.itemsPerPage)
      } else {
        return (this.itemsPerPage * this.page)
      }
    },
    productTotal() {
      let tmp = 0
      this.productLines.forEach(l => tmp += l.is_warranty ? 0 : l.product_obj.service_price * l.qty)
      return tmp
    },
    labourTotal() {
      let tmp = 0
      this.labourLines.forEach(l => tmp += l.is_warranty ? 0 : (l.labour_type_obj.service_price * (l.num_mins * l.num_workers / 60)))
      return tmp
    },
    invoiceTotal() {
      return this.productTotal + this.labourTotal + this.wo.minimum_price + (this.wo.timeline === 'BM8' ? 20 : 0)
    },
    processed() {
      if (this.wos.length) {
        let num = this.completed / this.wos.length * 100
        return num.toFixed(0)
      }
      return 0
    },
    allWOsProcessed() {
      return _.every(this.wos, ['office_complete', true])
    },
    stickies() {
      if (this.wo && this.wo.project && !this.isLoadingWO) {
        console.log(this.$refs.stickies.$data.stickies)
        return this.$refs.stickies.$data.stickies
      }
      return []
    },
    noBillingContact() {
      if (this.wo && this.wo.project && !this.wo.project_obj.bill_to) {
        return true
      } else {
        return false
      }
    },
    buttonText() {
      if (this.isLoadingWO) {
        return "Loading Work Order"
      } else if (this.stickies.length) {
        return "Please resolve stickies"
      } else if (this.noBillingContact) {
        return "No Billing Contact - Cannot Process"
      } else if (this.wo.wo_type_obj.requires_invoice === false) {
        return "Process W.O. (no invoice created)"
      }
      return "Create Invoice for this Work Order"
    },
    year() {
      return parseInt(moment().format('YYYY'))
    },
    div() {
      if (this.wo) {
        return this.wo.project ? this.wo.project_obj.division_obj : this.wo.estimate_obj.division_obj
      }
      return null
    },
    suNotes() {
      return _.filter(this.notes, { 'is_su_note': true, 'note_year': this.year, 'note_type': 'I' })
    }
  },
  methods: {
    async getData() {
      this.isLoading = true
      let tmpWOs = await wosAPI.getWOsReadyForOffice()
      this.wos = _.sortBy(tmpWOs, ['date_completed'])
      if (this.wos.length) {
        await this.open(this.wos[0])
      }
      this.isLoading = false
    },
    async open(item) {
      // Clear out any one time notes
      this.oneTimeNote = ''
      if (item.office_complete && item.invoiceID) {
        const link = document.createElement('a')
        const invURL = this.$router.resolve({ name: 'InvoiceOverview', params: { id: item.invoiceID } })
        link.target = '_blank'
        link.href = invURL.href
        link.click()
      } else if (item.office_complete) {
        return
      } else {
        this.isLoadingWO = true
        this.wo = await wosAPI.getWO(item.id)

        this.product = await assetsAPI.getInventoryForDivision(this.div.id)
        this.product = this.product.filter(p => p.archived === false)
        let tmp = await servicesAPI.getServicesForDivision(this.div.id)
        tmp = tmp.filter(t => t.archived === false)
        this.labour = tmp.filter(i => i.is_labour === true)
        this.disableGenerate = false

        this.productLines = await wosAPI.getProductForWO(this.wo.id, false);
        this.labourLines = await wosAPI.getLabourForWO(this.wo.id, false);
        this.notes = this.wo.project ? await irrigationAPI.getOneTimeNotes(this.wo.project_obj.id) : []
        if (this.div.division === 'Irrigation' && this.wo.wo_type_obj.category === 'SU') {
          let tmpNote = _.filter(this.notes, { 'note_year': this.year, 'is_su_note': true, 'note_type': 'I' })
          if (tmpNote.length > 0) { this.oneTimeNote = tmpNote[0].note }
        }
        if (this.div.division === 'Irrigation' && this.wo.wo_type_obj.category === 'BD') {
          let tmpNote = _.filter(this.notes, { 'note_year': this.year, 'is_su_note': false, 'note_type': 'I' })
          if (tmpNote.length > 0) {
            this.oneTimeNote = tmpNote[0].note
            this.wo.hold_invoice = true
            this.wo.hold_invoice_reason = "One Time Note: " + this.oneTimeNote + ". " + (this.wo.hold_invoice_reason ? this.wo.hold_invoice_reason : "")
            this.wo = await wosAPI.updateWO(this.wo)
          }
        }
        if (!this.wo.resolution && !!this.wo.wo_type_obj.default_resolution) {
          this.wo.resolution = this.wo.wo_type_obj.default_resolution
          this.wo = await wosAPI.updateWO(this.wo)
        }
        this.isLoadingWO = false
      }
    },
    first() {
      this.page = 1
    },
    prev() {
      this.page -= 1
    },
    next() {
      this.page += 1
    },
    last() {
      this.page = this.pageCount
    },
    updateInvoice(inv) {
      this.invoice = inv
    },
    async updateWO(woCopy) {
      this.isLoadingWO = true
      this.wo = await wosAPI.updateWO(woCopy)
      this.isLoadingWO = false
    },
    getClass(item) {
      if (item.office_complete && item.invoiceID) {
        return "green lighten-5 grey--text text--lighten-1"
      } else if (item.office_complete) {
        return "yellow lighten-5"
      } else if (this.wo && item.id === this.wo.id) {
        return "blue lighten-5"
      }
      return ""
    },
    async saveProductLine(line) {
      this.isSavingLine = true
      if (line.id) {
        const tmp = await wosAPI.updateProductForWO(line)
        this.productLines = miscServices.replaceObject(this.productLines, tmp)
      } else {
        line.work_order = this.wo.id
        const tmp = await wosAPI.createProductForWO(line)
        this.productLines.push(tmp)
      }
      this.isSavingLine = false
    },
    async saveLabourLine(line) {
      this.isSavingLine = true
      if (line.id) {
        const tmp = await wosAPI.updateLabourForWO(line)
        this.labourLines = miscServices.replaceObject(this.labourLines, tmp)
      } else {
        line.work_order = this.wo.id
        const tmp = await wosAPI.createLabourForWO(line)
        this.labourLines.push(tmp)
      }
      this.isSavingLine = false
    },
    async deleteProductLine(line) {
      this.isSavingLine = true
      this.lines = await wosAPI.deleteProductForWO(line)
      this.productLines = miscServices.removeObject(this.productLines, line)
      this.isSavingLine = false
    },
    async deleteLabourLine(line) {
      this.isSavingLine = true
      this.lines = await wosAPI.deleteLabourForWO(line.id)
      this.labourLines = miscServices.removeObject(this.labourLines, line)
      this.isSavingLine = false
    },
    async processWO() {
      this.disableGenerate = true
      let invoice;
      if (this.wo.wo_type_obj.requires_invoice) {
        invoice = await wosAPI.generateInvoiceFromWO(this.wo.id)
      } else {
        this.wo.office_complete = true
        await wosAPI.updateWO(this.wo)
      }

      let description = 'Work order processed.';
      description += this.wo.wo_type_obj.requires_invoice ? ' - Invoice Created' : ' (no invoice created)';

      const tracker = {
        'tracker_data': {
          'description': description,
          'taken_by': this.$store.state.auth.profile.id,
          'action_date': moment().format('YYYY-MM-DD').toString(),
        },
        'work_order': this.wo.id
      }

      const res = await trackerAPI.createWorkOrderTracker(tracker);
      this.$refs.actionTracker.pushItem(res);

      let index = _.findIndex(this.wos, w => w.id === this.wo.id);
      this.wos[index].office_complete = true;
      this.wos[index].invoiceID = invoice ? invoice.id : null;
      this.completed += 1;
    },
    getWOType(wo) {
      return _.find(this.woTypes, function (w) { return w.id === wo.wo_type })
    },
    getNoteTypeDisplay(noteType) {
      if (noteType === 'F') { return 'Field' }
      if (noteType === 'I') { return 'Invoicing' }
      if (noteType === 'S') { return 'Scheduling' }
      return '?'
    },
  },
  components: {
    'work-order-tracker': WorkOrderTracker,
    'wo-processing-lines': WOProcessingLines,
    'resolution': WOResolution,
    'crew-visits': CrewVisits,
    'stickies': Stickies,
    'outstanding-service': OutstandingService,
  }
}
</script>
